/*!
 * Copyright (c) REWE Digital GmbH
 */
import { h } from "@stencil/core";
/**
 * Input Component.
 */
export class VxInput {
    constructor() {
        /**
         * Handle Input change (for date inputs and so).
         */
        this.handleInputChange = (e) => {
            if (![
                'text',
                'number',
                'email',
                'password',
                'tel',
                'url',
                'search',
            ].includes(this.type)) {
                this.handleKeyUp(e);
            }
        };
        this.clearSearchInput = () => {
            this.value = '';
            /* Mock KeyUp Function */
            this.handleKeyUp({
                target: {
                    disabled: this.disabled,
                    name: this.name,
                    type: this.type,
                    value: this.value,
                },
            });
        };
        this.getSearchIcon = () => {
            var _a;
            if (this.value && ((_a = this.value) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                return h("div", { class: 'vx-input__icon vx-input__icon--close', onClick: this.clearSearchInput });
            }
            return h("div", { class: 'vx-input__icon vx-input__icon--lens' });
        };
        this.label = undefined;
        this.inputStyle = 'default';
        this.labelPosition = 'left';
        this.type = 'text';
        this.inputMode = undefined;
        this.autoCompleteType = undefined;
        this.required = false;
        this.name = undefined;
        this.inputId = undefined;
        this.placeholder = undefined;
        this.value = undefined;
        this.disabled = false;
        this.options = {};
        this.handleKeyUp = undefined;
    }
    /**
     * Renders the component.
     *
     * @returns {JSX.Element} - The rendered component.
     */
    render() {
        return (h("div", { key: '2a803c185722a4f72eef985a8a38bdbddfa42aed', class: `vx-input vx-input__label-position--${this.labelPosition} vx-input__style--${this.inputStyle}` }, this.label && h("span", { class: 'vx-input__label' }, this.label), h("input", Object.assign({ key: 'a3e30e1a5d0f8516ce95c8105f1b74b4d736e3c7', class: 'vx-input__input', id: this.inputId, type: this.type, name: this.name, inputMode: this.inputMode, autoComplete: this.autoCompleteType, required: this.required, placeholder: this.placeholder, disabled: this.disabled, onKeyUp: this.handleKeyUp, onChange: this.handleInputChange }, this.options, { value: this.value })), this.type === 'search' &&
            this.getSearchIcon()));
    }
    static get is() { return "vx-input"; }
    static get encapsulation() { return "scoped"; }
    static get originalStyleUrls() {
        return {
            "$": ["vx-input.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["vx-input.css"]
        };
    }
    static get properties() {
        return {
            "label": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Label of Checkbox."
                },
                "attribute": "label",
                "reflect": false
            },
            "inputStyle": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "'default' | 'minimal'",
                    "resolved": "\"default\" | \"minimal\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Style variant."
                },
                "attribute": "input-style",
                "reflect": false,
                "defaultValue": "'default'"
            },
            "labelPosition": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "'top' | 'left' | 'right'",
                    "resolved": "\"left\" | \"right\" | \"top\"",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Lable postion."
                },
                "attribute": "label-position",
                "reflect": false,
                "defaultValue": "'left'"
            },
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "InputTypes",
                    "resolved": "\"checkbox\" | \"color\" | \"date\" | \"datetime-local\" | \"email\" | \"file\" | \"hidden\" | \"image\" | \"month\" | \"number\" | \"password\" | \"radio\" | \"range\" | \"search\" | \"tel\" | \"text\" | \"time\" | \"url\" | \"week\"",
                    "references": {
                        "InputTypes": {
                            "location": "global",
                            "id": "global::InputTypes"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Input Types.\nMore info: https://better-mobile-inputs.netlify.app/."
                },
                "attribute": "type",
                "reflect": false,
                "defaultValue": "'text'"
            },
            "inputMode": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "InputModes",
                    "resolved": "\"decimal\" | \"email\" | \"none\" | \"numeric\" | \"search\" | \"tel\" | \"text\" | \"url\"",
                    "references": {
                        "InputModes": {
                            "location": "global",
                            "id": "global::InputModes"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "InputMode for forcing a specific keyboard style on mobile.\nMore info: https://better-mobile-inputs.netlify.app/."
                },
                "attribute": "input-mode",
                "reflect": false
            },
            "autoCompleteType": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "AutoCompleteTypes",
                    "resolved": "\"email\" | \"name\" | \"username\" | \"tel\" | \"url\" | \"on\" | \"off\" | \"honorific-prefix\" | \"given-name\" | \"additional-name\" | \"family-name\" | \"honorific-suffix\" | \"nickname\" | \"new-password\" | \"current-password\" | \"one-time-code\" | \"organization-title\" | \"organization\" | \"street-address\" | \"address-level1\" | \"address-level2\" | \"address-level3\" | \"address-level4\" | \"country\" | \"country-name\" | \"postal-code\" | \"cc-name\" | \"cc-given-name\" | \"cc-additional-name\" | \"cc-family-name\" | \"cc-number\" | \"cc-exp\" | \"cc-exp-month\" | \"cc-exp-year\" | \"cc-csc\" | \"cc-type\" | \"transaction-currency\" | \"transaction-amount\" | \"language\" | \"bday\" | \"bday-day\" | \"bday-month\" | \"bday-year\" | \"sex\" | \"tel-country-code\" | \"tel-national\" | \"tel-area-code\" | \"tel-local\" | \"tel-extension\" | \"impp\" | \"photo\"",
                    "references": {
                        "AutoCompleteTypes": {
                            "location": "global",
                            "id": "global::AutoCompleteTypes"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Autocomplete info for browser.\nMore info:  https://better-mobile-inputs.netlify.app/."
                },
                "attribute": "auto-complete-type",
                "reflect": false
            },
            "required": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Is Input Required."
                },
                "attribute": "required",
                "reflect": false,
                "defaultValue": "false"
            },
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Name of input."
                },
                "attribute": "name",
                "reflect": false
            },
            "inputId": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Input ID."
                },
                "attribute": "input-id",
                "reflect": false
            },
            "placeholder": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Name of input."
                },
                "attribute": "placeholder",
                "reflect": false
            },
            "value": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Value of input."
                },
                "attribute": "value",
                "reflect": false
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Disables checkbox."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "options": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "{\n        min?: number | string,\n        max?: number | string,\n    }",
                    "resolved": "{ min?: string | number; max?: string | number; }",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Optional Extra Values."
                },
                "defaultValue": "{}"
            },
            "handleKeyUp": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(e: KeyboardEvent | InputEvent | {\n        target: {\n            disabled: boolean\n            name: string\n            type: string\n            value: string\n        }\n    }) => void",
                    "resolved": "(e: KeyboardEvent | InputEvent | { target: { disabled: boolean; name: string; type: string; value: string; }; }) => void",
                    "references": {
                        "KeyboardEvent": {
                            "location": "global",
                            "id": "global::KeyboardEvent"
                        },
                        "InputEvent": {
                            "location": "global",
                            "id": "global::InputEvent"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Keyup in input.\nManuel trigger event option for clear input on search inputs."
                }
            }
        };
    }
}
//# sourceMappingURL=vx-input.js.map
